import React, { useEffect, useRef, useState } from 'react';
import { chefData } from '../data/mock';
import { Quote, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';

const Chef = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="chef"
      ref={sectionRef}
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#c9a962]/5 to-transparent" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#009246]/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left - Image */}
          <div
            className={`relative transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="relative">
              {/* Background frame */}
              <div className="absolute -inset-4 border-2 border-[#c9a962]/20 rounded-3xl" />
              <div className="absolute -inset-8 border border-[#c9a962]/10 rounded-3xl" />

              {/* Main image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={chefData.image}
                  alt={chefData.name}
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919]/60 via-transparent to-transparent" />
              </div>

              {/* Second image - floating */}
              <div className="absolute -bottom-8 -right-8 w-40 h-40 lg:w-56 lg:h-56 rounded-xl overflow-hidden shadow-2xl border-4 border-white animate-float">
                <img
                  src={chefData.image2}
                  alt={`${chefData.name} at work`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Italian flag accent */}
              <div className="absolute top-6 left-6 flex gap-1">
                <div className="w-2 h-16 bg-[#009246] rounded-full" />
                <div className="w-2 h-16 bg-white rounded-full" />
                <div className="w-2 h-16 bg-[#ce2b37] rounded-full" />
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <span className="text-[#c9a962] text-sm font-semibold tracking-[0.2em] uppercase">
              The Visionary
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e1919] mt-4 mb-2">
              {chefData.name}
            </h2>
            <p className="text-xl text-[#61525a] font-medium mb-8">
              {chefData.title}
            </p>

            <p className="text-lg text-[#736c64] leading-relaxed mb-10">
              {chefData.description}
            </p>

            {/* Quote */}
            <blockquote className="relative bg-white rounded-2xl p-8 shadow-lg border border-[#c9a962]/10">
              <Quote className="absolute -top-4 -left-4 w-10 h-10 text-[#c9a962] bg-white rounded-full p-2" />
              <p className="font-display text-xl text-[#1e1919] italic leading-relaxed">
                "{chefData.quote}"
              </p>
              <footer className="mt-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img
                    src={chefData.image}
                    alt={chefData.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-semibold text-[#1e1919]">{chefData.name}</p>
                  <p className="text-sm text-[#736c64]">Founder & Executive Chef</p>
                </div>
              </footer>
            </blockquote>

            {/* Credentials */}
            <div className="mt-10 flex flex-wrap gap-4">
              {[
                'Award-Winning Chef',
                'Italian Culinary Master',
                '20+ Years Experience',
              ].map((credential) => (
                <span
                  key={credential}
                  className="px-4 py-2 bg-[#1e1919] text-white text-sm font-medium rounded-full"
                >
                  {credential}
                </span>
              ))}
            </div>

            {/* View Gallery Button */}
            <div className="mt-10 flex justify-center lg:justify-center">
              <Link
                to="/chef-gallery"
                className="inline-flex items-center gap-3 bg-[#c9a962] text-[#1e1919] px-8 py-4 text-base font-semibold rounded transition-all duration-300 hover:bg-[#61525a] hover:text-white hover:shadow-xl hover:shadow-[#c9a962]/20 hover:-translate-y-1"
              >
                <Camera size={20} />
                Profile & Photo Gallery
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Chef;
